import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, ApiError } from '../api';
import { ConflictNotice } from '../ui/ConflictNotice';
import { DIGEST_KEY } from './summary';

type Channel = 'in_app' | 'push' | 'email';
type Settings = { enabled: boolean; hour: number; channels: Channel[]; version: number; quietFrom: string; quietTo: string };
type Draft = Pick<Settings, 'enabled' | 'hour' | 'channels'>;

const CHANNELS: Array<[Channel, string]> = [
  ['in_app', 'Dzwonek w aplikacji'],
  ['push', 'Telefon i komputer (push)'],
  ['email', 'E-mail'],
];
/** Every full hour; one inside the company's quiet hours sends at their end (as the server does). */
export const DIGEST_HOURS = Array.from({ length: 24 }, (_, i) => i);
/** Whether `hour` falls in the quiet hours [from, to) ('HH:MM', across midnight when from > to, none when equal). */
export function inQuiet(hour: number, from: string, to: string): boolean {
  if (from === to) return false;
  const t = `${String(hour).padStart(2, '0')}:00`;
  return from < to ? t >= from && t < to : t >= from || t < to;
}

/**
 * "Moje konto" → Powiadomienia → „Podsumowanie dzienne” (owner 2026-10-24 01:46): my own message with the counts of
 * "Podsumowanie" and every event without a quote, once a day at the chosen hour, on the chosen channels. Its own block
 * beside the push settings (the company's notification settings are in "Ustawienia firmy").
 */
export function DigestSettings() {
  const queryClient = useQueryClient();
  const current = useQuery({ queryKey: DIGEST_KEY, queryFn: () => api<Settings>('/api/me/daily-summary') });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (current.data) setDraft({ enabled: current.data.enabled, hour: current.data.hour, channels: current.data.channels });
  }, [current.data]);
  const save = useMutation({
    mutationFn: (d: Draft) => api<Settings>('/api/me/daily-summary', { method: 'PUT', body: { ...d, version: current.data!.version } }),
    onSuccess: (next) => {
      queryClient.setQueryData(DIGEST_KEY, next);
      setSaved(true);
    },
  });
  const error = save.error instanceof ApiError ? save.error : null;
  const set = (patch: Partial<Draft>) => {
    setSaved(false);
    setDraft((d) => (d ? { ...d, ...patch } : d));
  };
  const toggle = (c: Channel, on: boolean) => set({ channels: CHANNELS.map(([id]) => id).filter((id) => (id === c ? on : draft!.channels.includes(id))) });
  return (
    <section className="settings-section" id="podsumowanie-dzienne" data-feature-id="todo.digest" aria-labelledby="digest-title">
      <h2 id="digest-title">Podsumowanie dzienne</h2>
      <p className="hint">Raz dziennie wiadomość: ile czeka w „Podsumowaniu” i które wydarzenia nie mają wyceny (ze wszystkich dni). Gdy nic nie czeka — nie przychodzi.</p>
      {current.error && (
        <p className="alert" role="alert">
          Nie udało się wczytać ustawień podsumowania.
        </p>
      )}
      {draft && current.data && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate(draft);
          }}
        >
          <label className="check">
            <input type="checkbox" checked={draft.enabled} onChange={(e) => set({ enabled: e.target.checked })} /> Wysyłaj mi podsumowanie dzienne
          </label>
          <label className="field">
            <span>Godzina</span>
            <select value={draft.hour} disabled={!draft.enabled} onChange={(e) => set({ hour: Number(e.target.value) })}>
              {DIGEST_HOURS.map((h) => (
                <option key={h} value={h}>
                  {h}:00
                </option>
              ))}
            </select>
          </label>
          <fieldset className="field" disabled={!draft.enabled}>
            <legend>Gdzie</legend>
            {CHANNELS.map(([id, label]) => (
              <label key={id} className="check">
                <input type="checkbox" checked={draft.channels.includes(id)} onChange={(e) => toggle(id, e.target.checked)} /> {label}
              </label>
            ))}
          </fieldset>
          <p className="muted">
            Cisza nocna firmy (Ustawienia firmy → Powiadomienia): od {current.data.quietFrom} do {current.data.quietTo} nic nie przychodzi.
            {draft.enabled && inQuiet(draft.hour, current.data.quietFrom, current.data.quietTo) && ` Wybrana godzina wypada w ciszy nocnej — podsumowanie przyjdzie o ${current.data.quietTo}.`}
          </p>
          {error?.status === 409 && <ConflictNotice onReload={() => void current.refetch()} />}
          {error && error.status !== 409 && (
            <p className="alert" role="alert">
              {error.message}
            </p>
          )}
          <div className="actions">
            <button className="btn primary" type="submit" disabled={save.isPending || draft.channels.length === 0}>
              Zapisz
            </button>
            {saved && (
              <span className="muted" role="status">
                Zapisano
              </span>
            )}
          </div>
        </form>
      )}
    </section>
  );
}
